import { CSSProperties, useCallback, useEffect, useState } from "react";
import { Button, Typography } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DEFAULT_GENERAL_CONFIG_ORDER,
  GENERAL_CONFIG_SECTIONS,
  GeneralConfigSectionKey,
} from "../../utils/generalConfigSections";
import { getMyMenuOrder, updateMyMenuOrder } from "../../utils/menuOrderService";
import { Notify } from "../../utils/constants";

const sectionLabelByKey = new Map(
  GENERAL_CONFIG_SECTIONS.map((section) => [section.key, section.label]),
);

const SortableRow = ({ id }: { id: GeneralConfigSectionKey }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    marginBottom: 8,
    background: isDragging ? "#e6f4ff" : "#fafafa",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <span {...listeners} style={{ cursor: "grab", display: "flex" }}>
        <HolderOutlined />
      </span>
      {sectionLabelByKey.get(id)}
    </div>
  );
};

const ReorderGeneralConfigSections = () => {
  const [order, setOrder] = useState<GeneralConfigSectionKey[]>(
    DEFAULT_GENERAL_CONFIG_ORDER,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const loadOrder = useCallback(async () => {
    setIsLoading(true);
    try {
      const savedOrder = await getMyMenuOrder();
      setOrder(savedOrder.length ? savedOrder : DEFAULT_GENERAL_CONFIG_ORDER);
    } catch (error) {
      console.error("Failed to load section order:", error);
      Notify("error", "Could not load section order.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrder((previousOrder) => {
      const oldIndex = previousOrder.indexOf(
        active.id as GeneralConfigSectionKey,
      );
      const newIndex = previousOrder.indexOf(
        over.id as GeneralConfigSectionKey,
      );
      return arrayMove(previousOrder, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const savedOrder = await updateMyMenuOrder(order);
      setOrder(savedOrder.length ? savedOrder : order);
      Notify("success", "Section order updated successfully.");
    } catch (error) {
      console.error("Failed to update section order:", error);
      Notify("error", "Could not update section order.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="section-container" style={{ marginTop: 16 }}>
      <Typography.Title level={4}>Reorder Sections</Typography.Title>
      <Typography.Paragraph type="secondary">
        Drag to change the order these subsections appear in under General
        Configuration.
      </Typography.Paragraph>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          {order.map((key) => (
            <SortableRow key={key} id={key} />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type="primary"
        onClick={handleSave}
        loading={isSaving}
        disabled={isLoading}
      >
        Save order
      </Button>
    </div>
  );
};

export default ReorderGeneralConfigSections;
